import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

const EMPTY = { text: "", source: "Anonyme", active: true };
const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

export default function QuotesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/quotes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/quotes/${editingId}`, form);
        toast.success("Citation mise à jour");
      } else {
        await api.post("/admin/quotes", form);
        toast.success("Citation ajoutée");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ text: item.text, source: item.source, active: item.active });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette citation ?")) return;
    await api.delete(`/admin/quotes/${id}`).catch(() => {});
    toast.success("Citation supprimée");
    load();
  };

  return (
    <div data-testid="quotes-manager" className="grid grid-cols-1 gap-10 lg:grid-cols-2">
      <div>
        <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">
            {editingId ? "Modifier la citation" : "Ajouter une citation"}
          </h2>
          <div>
            <label className={labelClass}>Citation (proverbe, phrase de livre, humour...) *</label>
            <textarea data-testid="quote-text-input" rows="3" className={inputClass} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required />
          </div>
          <div>
            <label className={labelClass}>Source</label>
            <input data-testid="quote-source-input" className={inputClass} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Ex : Proverbe traditionnel" />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input data-testid="quote-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#B83227]" />
            Inclure dans la rotation quotidienne
          </label>
          <div className="flex gap-3">
            <button type="submit" data-testid="quote-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
              {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter"}
            </button>
            {editingId && (
              <button type="button" data-testid="quote-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
                Annuler
              </button>
            )}
          </div>
        </form>
        <p className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-xs leading-relaxed text-stone-500">
          Le bandeau rouge en haut du site affiche chaque jour une citation différente, choisie automatiquement parmi les citations actives. Ajoutez-en autant que vous voulez : plus il y en a, plus la rotation est variée.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Citations ({items.length})</h2>
        {items.map((q) => (
          <div key={q.id} data-testid={`quote-item-${q.id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm italic text-[#1E1B18]">« {q.text} »</p>
                <p className="mt-1 text-xs text-stone-500">— {q.source} · {q.active ? "Active" : "Inactive"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(q)} data-testid={`quote-edit-${q.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(q.id)} data-testid={`quote-delete-${q.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
